import {useQuery} from '@tanstack/react-query'
import moment from 'moment-timezone'
import {useCallback, useMemo} from 'react'

import {useDateTime} from '../../../../fields/dateTime/useDateTime'
import {useQuantity} from '../../../../fields/quantity/useQuantity'
import {
  AvailableTimeSlotsResult,
  processAvailabilityResponse,
  UseAvailableTimeSlotsParams
} from '../../../../slotManagement'
import {calculateAutoConfirmAvailability} from '../../../../slotManagement/availability/calculateAutoConfirmAvailability'
import {calculateSelectedSlotValidation} from '../../../../slotManagement/availability/calculateSelectedSlotValidation'
import {generateTimeSlots} from '../../../../slotManagement/availability/slotAvailabilityUtils'
import {useSlotManagementContext} from '../../../../slotManagement/context/useSlotManagementContext'
import {BuildSlotRequestResult} from '../../../../slotManagement/requests/buildSlotRequest'
import {useSlotRequest} from '../../../../slotManagement/requests/useSlotRequest'
import {useChangeableField, useChangeableFields} from '../../changeableFields'
import {resolveVariationCodeSlotReferenceDateTime} from '../resolveVariationCodeSlotReferenceDateTime'

/**
 * Converts site-timezone HH:mm slots to local-timezone HH:mm.
 * This ensures manual slots match the format of API-returned slots
 * (which are converted via moment(iso).format('HH:mm') → local timezone).
 */
const convertSlotsToLocalTimezone = (
  siteSlots: string[],
  timeZone: string | undefined,
  dateTime: string[] | undefined
): string[] => {
  if (!timeZone) return siteSlots
  const dateStr = dateTime?.[0]
    ? moment(dateTime[0]).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD')
  return siteSlots.map((slot) =>
    moment.tz(`${dateStr} ${slot}`, 'YYYY-MM-DD HH:mm', timeZone).local().format('HH:mm')
  )
}

/**
 * Order Change implementation of useAvailableTimeSlots.
 *
 * Gets configuration from SlotManagementContext (feature toggle, orgUnitId, siteNumber, orderId).
 *
 * Logic:
 * - If autoConfirmable: Generate slots manually from business hours (no API call)
 * - If not autoConfirmable: Fetch from API using provided query function
 *
 * Query is disabled when:
 * - Feature toggle is off
 * - Order is auto-confirmable (manual generation instead)
 * - Quantity exceeds max auto-confirmable
 * - Slot request params cannot be built (validation failed)
 *
 * @internal Use via SlotManagementContext
 */
export const useOrderChangeAvailableTimeSlots = ({
  dateIndex = 0,
  slotAvailabilityQueryFn
}: UseAvailableTimeSlotsParams): AvailableTimeSlotsResult => {
  const {config} = useSlotManagementContext()
  const {confirmationState} = useChangeableFields('orderChangeRulesOnly')
  const timeConfirmationState = useChangeableField(
    {field: 'dateTime', fieldPart: 'time'},
    'orderChangeRulesOnly'
  )
  const {businessHours, dateTime} = useDateTime()
  const quantityReturn = useQuantity()
  const isMaxAutoConfirmQuantityExceeded =
    quantityReturn.type === 'volume' ? quantityReturn.isMaxAutoConfirmQuantityExceeded : false

  // isAutoConfirmable from orderChangeRulesOnly state
  // check also if time changes are auto-confirmable
  const isAutoConfirmable =
    confirmationState === 'autoConfirmable' && timeConfirmationState.state === 'autoConfirmable'

  const resolveDateTime = useCallback(
    (currentDateTime: string) =>
      resolveVariationCodeSlotReferenceDateTime({
        currentDateTime,
        originalDateTime: config.originalDateTimeForSlotReference,
        variationCode: config.variationCode,
        variationCodeOffset: config.variationCodeOffset,
        variationCodeAdjustedTime: config.variationCodeAdjustedTime
      }),
    [
      config.originalDateTimeForSlotReference,
      config.variationCode,
      config.variationCodeAdjustedTime,
      config.variationCodeOffset
    ]
  )

  // Build slot request parameters
  const slotRequestResult: BuildSlotRequestResult = useSlotRequest({
    orgUnitId: config.orgUnitId,
    siteNumber: config.siteNumber,
    orderId: config.orderId,
    contractNumber: config.contractNumber,
    contractItemPositionNumber: config.contractItemPositionNumber,
    dateIndex,
    normalizeDate: true, // Availability endpoint needs normalized date (12:00 PM UTC)
    resolveDateTime
  })

  // Determine if we should call API
  const shouldQueryApi =
    config.isFeatureEnabled &&
    !isAutoConfirmable &&
    !isMaxAutoConfirmQuantityExceeded &&
    slotRequestResult.isValid &&
    slotAvailabilityQueryFn

  // Get query options and execute query
  const queryOptions =
    shouldQueryApi && slotRequestResult.params
      ? slotAvailabilityQueryFn(slotRequestResult.params)
      : null

  const queryInfo = useQuery(
    queryOptions ?? {
      queryKey: ['slot-availability-disabled'],
      queryFn: () => ({availableSlots: []}),
      enabled: false
    }
  )

  // Generate slots manually if auto-confirmable (memoized)
  // Convert from site timezone to local timezone so they match the API path format
  const manualSlots = useMemo(() => {
    if (!isAutoConfirmable || !businessHours) {
      return []
    }

    const siteSlots = generateTimeSlots({
      earliestPossibleLoadTime: businessHours.earliestPossibleLoadTime,
      latestPossibleLoadTime: businessHours.latestPossibleLoadTime
    })

    return convertSlotsToLocalTimezone(siteSlots, businessHours.timeZone, dateTime)
  }, [isAutoConfirmable, businessHours, dateTime])

  // Process API response
  const processedResponse = queryInfo.isSuccess
    ? processAvailabilityResponse(queryInfo?.data)
    : {availableSlots: [], isOutOfAutoConfirmLeadTime: false}

  // Determine available slots
  // Manual slots are already in HH:mm format, API slots need to be mapped from ISO to HH:mm
  const availableSlots = isAutoConfirmable
    ? manualSlots
    : (processedResponse?.availableSlots ?? []).map((slot) => moment(slot).format('HH:mm'))

  // Calculate auto-confirm availability status
  const autoConfirmAvailability = calculateAutoConfirmAvailability({
    queryError: queryInfo.isError,
    isQuantityExceeded: isMaxAutoConfirmQuantityExceeded,
    apiIsOutOfLeadTime: processedResponse.isOutOfAutoConfirmLeadTime,
    isManualSlots: isAutoConfirmable
  })

  const selectedDateTimeReference = dateTime?.[dateIndex]
    ? resolveDateTime(dateTime[dateIndex])
    : undefined

  // Get currently selected time in HH:mm format. Use the same resolved timestamp as the
  // availability request (before /available normalizes it to noon), so variation-code orders check
  // the adjusted slot returned by the API rather than the original form timestamp.
  const selectedDateTime = selectedDateTimeReference
    ? moment(selectedDateTimeReference).format('HH:mm')
    : ''

  // Calculate selected slot validation
  const selectedSlot = calculateSelectedSlotValidation({
    selectedDateTime,
    availableSlots,
    autoConfirmUnavailable: autoConfirmAvailability.isUnavailable
  })

  // Warn if params couldn't be built but feature is enabled
  if (config.isFeatureEnabled && !slotRequestResult.isValid) {
    console.warn(
      '[useOrderChangeAvailableTimeSlots] Could not build slot request params. Missing fields:',
      slotRequestResult.missingFields
    )
  }

  return {
    availableSlots,
    autoConfirmAvailability,
    selectedSlot,
    queryInfo
  }
}
